
import { stripHTML } from "@decocms/apps-website/utils/html"
import { removeProperties } from "~/sdk/format"
import { type SectionProps } from "~/types/deco"
import { getSiteSeo, isEagerRequest } from "@decocms/blocks/cms"
import {
    Offer,
    Product,
    ProductLeaf,
} from "@decocms/apps-commerce/types"

import URLtoBreadcrumb from "~/sdk/URLtoBreadcrumb"
import { capitalize, capitalizeWords } from "~/sdk/utils/capitalize"
import type {
    PageType,
    Props,
    StructuredData,
} from "./types"
import { getFinalPrice } from "~/sdk/price"
import { urlify } from "~/sdk/urlify"
import stripTrackingParams from "~/sdk/stripTrackingParams"
import { loadHtmlParser, resolveDescriptionForSchema } from "~/sdk/stripHtmlForStructuredData"
import { SITE } from "~/constants"
// import { formatPrice } from '$store/sdk/format.ts'

const MERCHANT_NAME = 'Miess' as const
const SCHEMA_LIST_PRICE = 'https://schema.org/ListPrice'
const SCHEMA_SALE_PRICE = 'https://schema.org/SalePrice'
const MERCHANT_RETURN_POLICY = {
    '@type': 'MerchantReturnPolicy',
    'applicableCountry': 'BR',
    '@id': '#return_policy',
    'merchantReturnDays': 7,
    'returnFees': 'https://schema.org/FreeReturn',
    'returnMethod': 'https://schema.org/ReturnByMail',
    'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
} as const

export const VTEX_MAX_PAGE = 50

// title/meta/link/OG/Twitter/prev/next are emitted once by the route-level
// `head()` (see ~/components/seo/seoHead.ts) from this section's loader
// output. Rendering them here as well would hoist a duplicated copy of every
// tag into <head> (DECO-5439/5440/5444).
export default function PageSEO(
    { jsonLD }: SectionProps<typeof loader>,
) {
    return (
        <>
            {jsonLD?.map((data: Record<string, unknown>, i: number) => (
                <script
                    key={(data['@type'] as string) ?? i}
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(data),
                    }}
                />
            ))}
        </>
    )
}
const KEYWORD_MAPPINGS = {
    'Brand': 'Marca',
    'Category': 'Categoria',
    'Department': 'Departamento',
    'SubCategory': 'Subcategoria',
    'Product': 'Produto',
    'Collection': 'Coleção',
    'Cluster': 'Coleção',
    'Search': 'Busca',
    'Search Root': 'Busca Raiz',
    'Search Category': 'Busca Categoria',
    'Unknown': '',
} as const
const renderTemplate = (template: string = '%s', value: string = '') =>
    template.replace('%s', value)

// Upstream `654db21ab` (c29064). O `storeURL` que a origem passa pelo
// MiessContext não existe aqui — o loader do alvo não recebe contexto — e lá o
// fallback já era `url.origin`, que é o que este projeto usa em todos os outros
// canonicals (CustomSEO/CustomSEOPDP/CustomSEOPLP).
const getCanonicalByURL = (requestUrl: string) => {
    const url = new URL(requestUrl)
    const origin = url.origin

    if (url.pathname === '/s') {
        const q = url.searchParams.get('q')
        if (q) {
            return `${origin}/s?q=${urlify(q.toLocaleLowerCase())}`
        }
        return `${origin}/s`
    }

    const base = `${origin}${url.pathname.toLowerCase()}`

    // Busca/filtro dentro de categoria — canonical aponta para a URL base
    if (url.searchParams.has('q')) {
        return base
    }

    const page = url.searchParams.get('page')
    if (page && page !== '1') {
        return `${base}?page=${page}`
    }

    return base
}

const checkSearchCategory = (pageTypes: PageType[]) => {
    return pageTypes.includes('Department') && pageTypes.includes('Unknown')
}

const addMerchantReturnPolicyRefatory = (product: Product): Product => {
    const priceSpecificationShow = [SCHEMA_LIST_PRICE, SCHEMA_SALE_PRICE]

    delete product?.offers

    const group = product.isVariantOf as
        | (Product['isVariantOf'] & { description?: string; name?: string })
        | undefined
    const groupDescription = group?.description
    const groupName = group?.name

    const rootDescription = resolveDescriptionForSchema(
        product.description,
        groupDescription,
        product.name,
    )

    const productGroupDescription = resolveDescriptionForSchema(
        groupDescription,
        product.description,
        groupName,
        product.name,
    ) || rootDescription

    return {
        ...product,
        description: rootDescription,
        isVariantOf: product.isVariantOf
            ? {
                ...product.isVariantOf,
                description: productGroupDescription,
                hasVariant: product.isVariantOf.hasVariant.map((variant) => {
                    const firstOffer = variant.offers?.offers?.[0]
                    const { seller: _catalogSeller, ...offerFields } = (firstOffer ??
                        {}) as Offer & { seller?: unknown }

                    const priceSpecification = firstOffer?.priceSpecification?.filter(
                        (item) =>
                            priceSpecificationShow.includes(item.priceType) &&
                            !item.name,
                    ).map((item) => ({
                        ...item,
                        priceCurrency: firstOffer?.priceCurrency ?? 'BRL',
                    })) || []

                    const variantName = variant.name ?? product.name
                    const variantDescription = resolveDescriptionForSchema(
                        variant.description,
                        product.description,
                        groupDescription,
                        variantName,
                        product.name,
                    )

                    return {
                        ...variant,
                        image: variant.image?.[0].url,
                        video: undefined,
                        description: variantDescription,
                        offers: {
                            ...offerFields,
                            itemCondition: firstOffer?.itemCondition ??
                                'https://schema.org/NewCondition',
                            priceCurrency: firstOffer?.priceCurrency ?? 'BRL',
                            priceSpecification: priceSpecification,
                            sku: variant.sku,
                            url: variant.url,
                            seller: {
                                '@type': 'Organization',
                                name: MERCHANT_NAME,
                            },
                            'hasMerchantReturnPolicy': MERCHANT_RETURN_POLICY,
                            price: firstOffer?.price,
                        },
                    }
                }) as unknown as ProductLeaf[],
            }
            : undefined,
    }
}

const addMerchantReturnPolicy = (product: Product): Product => ({
    ...product,
    isVariantOf: product.isVariantOf
        ? {
            ...product.isVariantOf,
            hasVariant: product.isVariantOf.hasVariant.map((p) => addMerchantReturnPolicy(p)),
        }
        : undefined,
    offers: {
        ...product.offers,
        priceCurrency: 'BRL',
        'hasMerchantReturnPolicy': MERCHANT_RETURN_POLICY,
        availability: product.offers?.offers[0].availability ?? 'https://schema.org/InStock',
        offers: product.offers?.offers?.map((offer) => ({
            ...offer,
            priceSpecification: offer.priceSpecification?.map((spec) => ({
                ...spec,
                priceCurrency: 'BRL',
            })),
            priceCurrency: 'BRL',
            'hasMerchantReturnPolicy': MERCHANT_RETURN_POLICY,
        })) ?? [],
    } as Product['offers'],
})

// Ajustar no codigo depois dos testes, colocar essa funcao como utilitario
const bankersRound = (num: number, decimalPlaces: number) => {
    const d = decimalPlaces || 0
    const m = Math.pow(10, d)
    const n = +(d ? num * m : num).toFixed(8) // Avoid rounding errors
    const i = Math.floor(n), f = n - i
    const e = 1e-8 // Allow for rounding errors in f
    const r = (f > 0.5 - e && f < 0.5 + e) ? ((i % 2 === 0) ? i : i + 1) : Math.round(n)
    return d ? r / m : r
}

/** Preço promocional (ex.: PIX) no Offer e em SalePrice; ListPrice mantém o valor de tabela. */
const applyPixPriceToInnerOffer = (offer: Offer, salePrice: number): Offer => {
    const rounded = Number(salePrice.toFixed(2))
    const mappedSpecs = offer.priceSpecification?.map((spec) => {
        if (spec.priceType === SCHEMA_SALE_PRICE && !spec.name) {
            return {
                ...spec,
                price: bankersRound(salePrice, 2),
                priceCurrency: 'BRL',
            }
        }
        if (spec.priceType === SCHEMA_LIST_PRICE && !spec.name) {
            return {
                ...spec,
                priceCurrency: 'BRL',
            }
        }
        return {
            ...spec,
            priceCurrency: spec.priceCurrency ?? 'BRL',
        }
    })
    return {
        ...offer,
        price: rounded,
        priceCurrency: 'BRL',
        priceSpecification: mappedSpecs,
    }
}

const mapAggregateOffers = (
    aggregate: Product['offers'],
    mapOffer: (o: Offer) => Offer,
): Product['offers'] => {
    if (!aggregate?.offers?.length) return aggregate
    return {
        ...aggregate,
        offers: aggregate.offers.map(mapOffer),
    }
}

const addPricePix = (finalPrice: number, product: Product): Product => {
    const aggregate = product.offers
    return {
        ...product,
        isVariantOf: product.isVariantOf
            ? {
                ...product.isVariantOf,
                hasVariant: product.isVariantOf.hasVariant.map((p) =>
                    addPricePix(finalPrice, p)
                ) as unknown as ProductLeaf[],
            }
            : undefined,
        offers: mapAggregateOffers(
            aggregate,
            (o) => applyPixPriceToInnerOffer(o, finalPrice),
        ),
    } as Product
}

const addCurrencyPriceOnListPrice = (product: Product): Product => {
    const aggregate = product.offers
    return {
        ...product,
        isVariantOf: product.isVariantOf
            ? {
                ...product.isVariantOf,
                hasVariant: product.isVariantOf.hasVariant.map((p) =>
                    addCurrencyPriceOnListPrice(p)
                ) as unknown as ProductLeaf[],
            }
            : undefined,
        offers: mapAggregateOffers(aggregate, (offer) => ({
            ...offer,
            priceSpecification: offer.priceSpecification?.map((spec) => ({
                ...spec,
                priceCurrency: 'BRL',
            })),
        })),
    } as Product
}

const checkSearchRoot = (pageTypes: PageType[], requestUrl: string) => {
    const url = new URL(requestUrl)
    const hasQParam = url.searchParams.has('q')
    return pageTypes.includes('Search') && !hasQParam
}
const updatePageTypes = (pageTypes: PageType[], request: Request) => {
    if (!pageTypes || !request?.url) {
        return
    }
    const isSearchCategory = checkSearchCategory(pageTypes)
    const isSearchRoot = checkSearchRoot(pageTypes, request.url)
    if (isSearchCategory) {
        pageTypes.push('Search Category')
    }
    if (isSearchRoot) {
        pageTypes.push('Search Root')
    }
}
const getSearchPageTitle = (pageTypes: PageType[], request: Request) => {
    const url = new URL(request.url)
    const isSearchRoot = checkSearchRoot(pageTypes, request.url)
    if (isSearchRoot) {
        const title = decodeURIComponent(url.pathname).replace(/(^\/|-)/g, ' ')
        const searchPageTitle = capitalize(title)
        return searchPageTitle
    }
    const searchQuery = url.searchParams.get('q')
    const searchPageTitle = capitalize(searchQuery ?? '')
    if (!searchPageTitle) {
        const breadcrumbArray = URLtoBreadcrumb(request.url).map(({ name }) =>
            name && capitalizeWords(name)
        ).reverse()
        const title = breadcrumbArray.filter(Boolean).join(' - ')
        return title
    }
    return searchPageTitle
}
const getSearchPageDescription = (pageTypes: PageType[], request: Request) => {
    const url = new URL(request.url)
    const isSearchRoot = checkSearchRoot(pageTypes, request.url)
    if (isSearchRoot) {
        const title = decodeURIComponent(url.pathname).replace(/(^\/|-)/g, ' ')
        const searchPageTitle = capitalize(title)
        return searchPageTitle
    }
    const searchQuery = url.searchParams.get('q')
    const searchPageTitle = capitalize(searchQuery ?? '')
    return searchPageTitle
}
const generateKeywords = (
    keywords: string = '',
    pageType: keyof typeof KEYWORD_MAPPINGS = 'Unknown',
) => [KEYWORD_MAPPINGS[pageType], keywords].filter(Boolean).join(', ')
/**
 * Reescreve uma URL do loader para o host da LOJA.
 *
 * O `baseUrl` que os loaders da VTEX recebem é
 * `https://<account>.vtexcommercestable.com.br`, então todo `url`/`item` que
 * sai deles aponta para o host da VTEX — e o JSON-LD ia indexado com ele
 * (verificado no HTML servido ao Googlebot: `item` do breadcrumb em
 * `miess.vtexcommercestable.com.br`). Path e query são preservados; só a
 * origem troca.
 */
const rebaseUrl = (raw?: string): string | undefined => {
    if (!raw) return undefined
    try {
        const parsed = new URL(raw, SITE.URL)
        return new URL(`${parsed.pathname}${parsed.search}`, SITE.URL).href
    } catch {
        return undefined
    }
}

/** `rebaseUrl` em todo `url`/`item` string de um nó de JSON-LD, um nível fundo. */
const rebaseJsonLdUrls = (node: Record<string, unknown>): Record<string, unknown> => {
    const fix = (v: unknown): unknown => {
        if (typeof v === 'string') return rebaseUrl(v) ?? v
        if (Array.isArray(v)) return v.map((item) => (item && typeof item === 'object'
            ? rebaseJsonLdUrls(item as Record<string, unknown>)
            : item))
        return v
    }
    const out: Record<string, unknown> = { ...node }
    for (const key of ['url', 'item', 'itemListElement'] as const) {
        if (key in out) out[key] = fix(out[key])
    }
    return out
}

const getJsonLD = async (structuredData: StructuredData) => {
    if (!structuredData) {
        return []
    }
    const clonedData = structuredClone(structuredData)
    const PRODUCT_FIELDS_TO_REMOVE = [
        'sellerName',
        'teasers',
        'required',
        'additionalProperty',
        'questions',
    ]
    if (clonedData['@type'] === 'ProductDetailsPage') {
        const product = clonedData.product

        const { finalPrice } = getFinalPrice(product.offers)
        const productWithPricePix = addPricePix(
            finalPrice,
            product,
        ) as unknown as Product
        const productWithCurrencyListPrice = addCurrencyPriceOnListPrice(
            productWithPricePix,
        ) as unknown as Product

        // Spread first, then override: with the spread last the SEO fallback
        // description was always clobbered by the (possibly empty) product one.
        const productWithMerchantReturnPolicy = addMerchantReturnPolicyRefatory({
            ...productWithCurrencyListPrice,
            description: productWithCurrencyListPrice.description ||
                clonedData.seo?.description,
        })

        const productToJsonLD = {
            '@context': 'https://schema.org',
            ...removeProperties(productWithMerchantReturnPolicy, PRODUCT_FIELDS_TO_REMOVE),
        }

        const breadcrumbListToJsonLD = {
            '@context': 'https://schema.org',
            ...clonedData.breadcrumbList,
        }

        return [breadcrumbListToJsonLD, productToJsonLD]
    }
    if (clonedData['@type'] === 'ProductListingPage') {
        const products = clonedData.products

        if (!products?.length) {
            return []
        }
        // const LIMIT_JSONLD_PRODUCTS = 5
        // const sliced = products.slice(0, LIMIT_JSONLD_PRODUCTS)

        const rawBreadcrumb = clonedData.breadcrumb as unknown as
            | Record<string, unknown>
            | null
            | undefined
        const breadcrumbSource: Record<string, unknown> = rawBreadcrumb &&
                typeof rawBreadcrumb === 'object'
            ? rawBreadcrumb
            : {}
        const { '@context': _listingBreadcrumbCtx, ...breadcrumbNode } = breadcrumbSource

        const graph: Record<string, unknown>[] = []

        if (breadcrumbNode['@type']) {
            graph.push(rebaseJsonLdUrls(breadcrumbNode))
        }

        // Página de listagem é página-RESUMO: o schema.org que um crawler
        // consome aqui é um `ItemList` de `ListItem` apontando para as PDPs,
        // que é onde o `Product` completo vive.
        //
        // O que estava aqui antes: `{ '@type': 'Products', products: [...] }`
        // com o resultado do loader inteiro embutido. `Products` não existe no
        // schema.org, então nenhum crawler converte esse nó em entidade —
        // medido numa PLP real, 2,75 MB dos 2,47 MB do bloco `ld+json` (e dos
        // 6 MB de HTML que o Googlebot recebia) era peso morto integral. O
        // pipeline de enriquecimento por produto (addPricePix,
        // addCurrencyPriceOnListPrice, addMerchantReturnPolicyRefatory,
        // removeProperties) rodava 36x por request para produzir isso; segue
        // valendo, e é usado, no ramo da PDP acima.
        const itemListElement = products.flatMap((p, index) => {
            const url = rebaseUrl((p.isVariantOf?.url ?? p.url) as string | undefined)
            if (!url) return []
            return [{
                '@type': 'ListItem',
                position: index + 1,
                url,
                name: p.isVariantOf?.name ?? p.name,
            }]
        })

        if (itemListElement.length) {
            graph.push({
                '@type': 'ItemList',
                numberOfItems: itemListElement.length,
                itemListElement,
            })
        }

        return [{
            '@context': 'https://schema.org',
            '@graph': graph,
        }]
    }
    return []
}
const hasSpecificationFiltersWithOutColor = (request: Request) => {
    const url = new URL(request.url)
    const mapParams = url.searchParams.get('map')
    if (!mapParams) return false
    return mapParams.split(',').filter((item) => item !== 'specificationFilter_24').some((param) =>
        /^specificationFilter/.test(param)
    )
}
const hasColorFilter = (request: Request) => {
    const url = new URL(request.url)
    const mapParams = url.searchParams.get('map')
    if (!mapParams) return false
    return mapParams.split(',').some((param) => /^specificationFilter_24/.test(param))
}
const hasPriceFilters = (request: Request) => {
    const url = new URL(request.url)
    const mapParams = url.searchParams.get('map')
    if (!mapParams) return false
    return mapParams.split(',').some((param) => /^priceFrom/.test(param))
}
const isBrandPage = (request: Request) => {
    const url = new URL(request.url)
    const mapParam = url.searchParams.get('map')
    return mapParam ? mapParam.split(',').includes('b') : false
}
export interface brands {
    active: boolean
    id: number
    name: string
    slug: string
}

const getEmptyPageTitle = (
    url: string,
    structuredData: StructuredData | undefined,
) => {
    const requestUrl = new URL(url ?? "/", SITE.URL)
    const emptyProductPage = !structuredData?.['@type'] && requestUrl.pathname.includes('/p')
    const emptySearchPage = requestUrl.pathname.includes('/buscavazia')

    if (emptyProductPage) return `Busca não encontrada | ${requestUrl.pathname}${requestUrl.search}`

    if (emptySearchPage) {
        const searchTerm = requestUrl.searchParams.get('t')
        if (searchTerm) return `Busca não encontrada | ${searchTerm}`
    }

    return undefined
}

const getEmptyPageDescription = (
    url: string,
    structuredData: StructuredData | undefined,
) => {
    const requestUrl = new URL(url ?? "/", SITE.URL)
    const emptyProductPage = !structuredData?.['@type'] && requestUrl.pathname.includes('/p')
    const emptySearchPage = requestUrl.pathname.includes('/buscavazia')

    if (emptyProductPage || emptySearchPage) {
        return 'Não encontramos resultados para sua busca. Tente outros termos ou navegue pelas categorias disponíveis.'
    }

    return undefined
}

export const loader = async (
    {
        title,
        titleTemplate,
        description,
        descriptionTemplate,
        keywords,
        canonical,
        favicon,
        noIndex,
        noFollow,
        og,
        image,
        structuredData,
        omitProductsFromStructuredData,
        listingPage,
    }: Props,
    request: Request,
) => {
    await loadHtmlParser()
    const globalSeo = getSiteSeo() as ReturnType<typeof getSiteSeo> & { type?: string }
    const type = og?.type || globalSeo?.type || 'website'
    let robots = globalSeo?.noIndexing
        ? 'noindex,nofollow'
        : [noIndex ? 'noindex' : 'index', noFollow ? 'nofollow' : 'follow']
            .filter(Boolean).join(',')
    let nextPage: string | undefined
    let prevPage: string | undefined
    let jsonLD: Record<string, unknown>[] = []

    try {
        const pageType = structuredData?.['@type']
        const seo = structuredData?.seo
        const currentTemplateTitle = titleTemplate || globalSeo?.titleTemplate
        const emptyPageTitle = getEmptyPageTitle(request.url, structuredData)
        const currentTitle = emptyPageTitle || title || seo?.title || globalSeo?.title
        const currentTemplateDescription = descriptionTemplate || globalSeo?.descriptionTemplate
        const emptyPageDescription = getEmptyPageDescription(request.url, structuredData)
        // `seo.description` da PDP vem do `metaTagDescription` do VTEX, que na
        // Miess é HTML (`<p>`, `<strong>`, `<BR>`) — e sai escapado no
        // `<meta name="description">`. O loader Fresh antigo devolvia esse campo
        // vazio, então a PDP simplesmente não tinha description e o HTML nunca
        // aparecia; o `@decocms/apps-vtex` preenche, e a tag passou a ir com
        // markup. Mesmo `stripHTML` que CustomSEO/CustomSEOPDP já aplicam.
        const currentDescription = stripHTML(
            emptyPageDescription || description || seo?.description ||
                globalSeo?.description || '',
        )
        title = renderTemplate(currentTemplateTitle, currentTitle)
        description = renderTemplate(currentTemplateDescription, currentDescription)
        // Em produção o PageSEO recebe uma request.url já filtrada pelo
        // blockedQs (via createStableHref), então a canonical nunca via
        // tracking/PII. Aqui esse filtro não existe no pipeline, e como
        // getCanonicalByURL herda a cauda da query (split('page=')), a PII
        // caía dentro da canonical — que é justamente o link indexado.
        canonical = canonical || getCanonicalByURL(stripTrackingParams(new URL(request.url)).href)
        favicon = favicon || globalSeo?.favicon
        image = image || globalSeo?.image
        const themeColor = globalSeo?.themeColor || '#FFFFFF'
        const card = type === 'website' ? 'summary' : 'summary_large_image'

        if (pageType === 'ProductListingPage' && structuredData) {
            const { currentPage = 0, previousPage: prevPageInfo, nextPage: nextPageInfo } =
                structuredData.pageInfo ??
                    {}

            // rel=prev/next não devem repassar tracking/PII da query string
            // entrante (utm_*, gclid, ?email=…) — DECO-5819.
            const previousUrl = stripTrackingParams(new URL(request.url))
            const nextUrl = stripTrackingParams(new URL(request.url))

            if (prevPageInfo) {
                if (currentPage > 2) {
                    previousUrl.searchParams.set('page', (currentPage - 1).toString())
                } else {
                    previousUrl.searchParams.delete('page')
                }
                prevPage = previousUrl.toString()
            }

            if (nextPageInfo && currentPage < VTEX_MAX_PAGE) {
                nextUrl.searchParams.set('page', (currentPage + 1).toString())
                nextPage = nextUrl.toString()
            }

            const pageTypes: PageType[] = structuredData.pageInfo.pageTypes ?? []
            updatePageTypes(pageTypes, request)
            keywords = generateKeywords(keywords, pageTypes.at(-1))
            title = renderTemplate('%s', currentTitle)
            const isSearchPage = pageTypes.includes('Search') ||
                pageTypes.includes('Search Category')
            const hasInvalidPageType = pageTypes.includes('Unknown')

            const isSearchRoot = pageTypes.includes('Search Root')

            // console.log(title)
            // console.log(hasInvalidPageType)

            if (hasInvalidPageType) {
                // const currentSearchTemplate = listingPage?.search?.titleTemplate
                // const currentSearchTitle = getSearchPageTitle(pageTypes, request)
                // console.log(currentSearchTitle, renderTemplate(currentSearchTemplate, currentSearchTitle))
                // title = renderTemplate(currentSearchTemplate, currentSearchTitle)
                robots = 'index, follow'
            }
            if (isSearchPage) {
                const isBrand = isBrandPage(request)
                const hasSpecificationFiltersOnPage = hasSpecificationFiltersWithOutColor(request)
                const hasPriceFiltersOnPage = hasPriceFilters(request)
                const hasColorFilterOnPage = hasColorFilter(request)
                if (isBrand) robots = 'index, follow'
                if (hasSpecificationFiltersOnPage || hasPriceFiltersOnPage) {
                    robots = 'noindex, follow'
                }
                if (
                    hasColorFilterOnPage && !hasPriceFiltersOnPage && !hasSpecificationFiltersOnPage
                ) {
                    robots = 'index, follow'
                }
                const currentSearchTemplate = listingPage?.search?.titleTemplate
                const currentSearchTitle = getSearchPageTitle(pageTypes, request)
                // console.log(currentSearchTitle)
                title = renderTemplate(currentSearchTemplate, currentSearchTitle)
                const currentSearchDescriptionTemplate = listingPage?.search?.descriptionTemplate
                const currentSearchDescription = getSearchPageDescription(pageTypes, request)
                description = renderTemplate(
                    currentSearchDescriptionTemplate,
                    currentSearchDescription,
                )
            }
            if (isSearchRoot) robots = 'noindex, follow'

            if (omitProductsFromStructuredData) {
                structuredData = structuredClone(structuredData)
                structuredData.products = []
                structuredData.filters = []
            }
        } else if (pageType === 'ProductDetailsPage' && structuredData) {
            keywords = generateKeywords(keywords, 'Product')

            if (omitProductsFromStructuredData) {
                structuredData = structuredClone(structuredData)
                structuredData.product.isVariantOf = undefined
                structuredData.product.isSimilarTo = structuredData.product
                    .isSimilarTo?.map((item) => ({ ...item, isVariantOf: undefined }))
                structuredData.product.isRelatedTo = structuredData.product
                    .isRelatedTo?.map((item) => ({ ...item, isVariantOf: undefined }))
            }
        }
        // JSON-LD structured data is only consumed by crawlers. For real users
        // skip building/serializing it — the full ProductListingPage/Product
        // schema (with all variants) is hundreds of KB of dead payload in the
        // hydration blob. Bots and the ?__deco_ssr=1 audit override still get it.
        // The <head> (title/meta/canonical/OG) below is built for everyone.
        if (
            isEagerRequest({
                userAgent: request.headers.get("user-agent") ?? undefined,
                url: request.url,
                request,
            })
        ) {
            jsonLD = await getJsonLD(structuredData)
        }
        const meta = [
            { name: 'description', content: description },
            { name: 'keywords', content: keywords },
            { name: 'robots', content: robots },
            { name: 'theme-color', content: themeColor },
        ].filter(({ content }) => !!content)
        const link = [
            { rel: 'canonical', href: canonical },
            { rel: 'shortcut icon', href: favicon },
        ].filter(({ href }) => !!href)
        // `name` E `property` no MESMO objeto: o Open Graph é lido por `property`
        // (é o que o SeoV2 da página emitia no Fresh), e o TanStack deduplica meta
        // por `name ?? property` — duas entradas separadas viram uma só.
        const opengraph = [
            { name: 'og:title', property: 'og:title', content: title },
            { name: 'og:description', property: 'og:description', content: description },
            { name: 'og:type', property: 'og:type', content: type },
            { name: 'og:image', property: 'og:image', content: image },
            { name: 'og:url', property: 'og:url', content: canonical },
        ].filter(({ content }) => !!content)
        const twitter = [
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: image },
            { name: 'twitter:card', content: card },
        ].filter(({ content }) => !!content)

        return {
            title,
            meta,
            link,
            opengraph,
            twitter,
            jsonLD,
            nextPage,
            prevPage,
        }
    } catch (error) {
        console.error('PageSEO loader error:', error)
        const defaultTitle = title || 'Miess'
        const defaultDescription = description || ''
        const defaultCanonical = canonical || getCanonicalByURL(request.url)
        const defaultImage = image || ''
        const defaultThemeColor = globalSeo?.themeColor || '#FFFFFF'
        const defaultType = type || 'website'
        const defaultCard = defaultType === 'website' ? 'summary' : 'summary_large_image'

        return {
            title: defaultTitle,
            meta: [
                { name: 'description', content: defaultDescription },
                { name: 'keywords', content: keywords || '' },
                { name: 'robots', content: robots || 'noindex, nofollow' },
                { name: 'theme-color', content: defaultThemeColor },
            ].filter(({ content }) => !!content),
            link: [
                { rel: 'canonical', href: defaultCanonical },
                { rel: 'shortcut icon', href: favicon || '' },
            ].filter(({ href }) => !!href),
            opengraph: [
                { name: 'og:title', property: 'og:title', content: defaultTitle },
                { name: 'og:description', property: 'og:description', content: defaultDescription },
                { name: 'og:type', property: 'og:type', content: defaultType },
                { name: 'og:image', property: 'og:image', content: defaultImage },
                { name: 'og:url', property: 'og:url', content: defaultCanonical },
            ].filter(({ content }) => !!content),
            twitter: [
                { name: 'twitter:title', content: defaultTitle },
                { name: 'twitter:description', content: defaultDescription },
                { name: 'twitter:image', content: defaultImage },
                { name: 'twitter:card', content: defaultCard },
            ].filter(({ content }) => !!content),
            jsonLD: jsonLD || [],
            nextPage: nextPage || '',
            prevPage: prevPage || '',
        }
    }
}

export function LoadingFallback() {
    return (
        <div className='h-0 w-0'>
        </div>
    )
}
